import { CrossIcon } from "$components/icons";
import { Table, type TableColumnDefinition } from "$components/table";
import { RHFTextField } from "$routes/inventory/_lib/rhf-text-field";
import SkuSearchInput, {
  OptionWithStockLevel,
} from "$routes/inventory/_lib/sku-search-input";
import { IconButton, makeStyles } from "@material-ui/core";
import type { ClassNameMap } from "@material-ui/core/styles/withStyles";
import React from "react";
import { useIntl } from "react-intl";
import { useSkuRequestFormState } from "./use-sku-request-form-state";

function buildTableColumns({
  formatMessage,
  classes,
  remove,
  totalRows,
}: {
  formatMessage: ReturnType<typeof useIntl>["formatMessage"];
  classes: ClassNameMap;
  remove: ReturnType<typeof useSkuRequestFormState>["remove"];
  totalRows: number;
}): TableColumnDefinition[] {
  return [
    {
      field: "skuId",
      headerName: formatMessage({
        id: "inventory.moveMedication.requestNew.columnLabel.item",
        defaultMessage: "ITEM",
      }),
      flex: 16,
      renderCell: (_value, index) => {
        return (
          <SkuSearchInput
            name={`skus.${index}.sku`}
            analyticsEventName={"inventoryMoveRequest.newMoveRequest.skuSearch"}
            classes={{
              root: classes.hiddenHelperText,
              inputRoot: classes.inputCellRoot,
            }}
            renderOption={(option) => <OptionWithStockLevel {...option} />}
          />
        );
      },
    },
    {
      field: "quantity",
      headerName: formatMessage({
        id: "inventory.moveMedication.requestNew.columnLabel.quantity",
        defaultMessage: "QUANTITY",
      }),
      flex: 4,
      renderCell: (_value, index) => {
        return (
          <RHFTextField
            name={`skus.${index}.quantity`}
            inputProps={{ type: "number" }}
            classes={{
              root: `${classes.hiddenHelperText} ${classes.textFieldRoot}`,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      flex: 1,
      renderCell: (_value, index) => {
        // Don't show the cross for the final row, as a new row will appear if populated
        if (index === totalRows - 1) {
          return null;
        }
        return (
          <IconButton
            aria-label="edit"
            aria-controls="edit"
            onClick={() => {
              remove(index);
            }}
          >
            <CrossIcon fill="#004F71" />
          </IconButton>
        );
      },
    },
  ];
}

const useStyles = makeStyles((theme) => ({
  rowCellClass: {
    padding: theme.spacing(1),
  },
  inputCellRoot: {
    height: "56px",
  },
  hiddenHelperText: {
    "& .MuiFormHelperText-root": {
      display: "none",
    },
  },
  inputCellInput: {
    padding: "8px 0px",
  },
  textFieldRoot: {
    "& .MuiOutlinedInput-input": {
      padding: "8px 0px",
    },
  },
}));

const SkuRequestTable: React.FunctionComponent = () => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { fields, remove } = useSkuRequestFormState();
  const columns = buildTableColumns({
    formatMessage,
    classes,
    remove,
    totalRows: fields.length,
  });

  return (
    <Table
      columns={columns}
      rows={fields}
      classes={{ rowCellClass: () => classes.rowCellClass }}
    />
  );
};

export default SkuRequestTable;
